import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'

import { Button } from '@/components/ui/Button'
import { sendAuctionNotifyTest } from '@/api/auction-sms'
import { dateFnsLocale } from '@/utils/dateLocale'

/**
 * «Тест: SMS и звонок о торгах» в палитре лида СберАвто (только админ, решение
 * владельца 05.11; другие воронки не звонят): по данным лида, на любом этапе, только тестовым
 * контактам из «Сбер → SMS и звонки». SMS всем, звонок тем, у кого канал
 * «SMS + звонок». Звонок ждёт окна звонков.
 */
export function AuctionNotifyTestButton({ leadId }: { leadId: string }) {
  const { t } = useTranslation()
  const send = useMutation({
    mutationFn: async () => {
      const res = await sendAuctionNotifyTest(leadId)
      // Поставлен вне окна звонков: показываем, когда позвонит.
      return { ...res, waits: res.calls > 0 && Date.parse(res.not_before) > Date.now() + 60_000 }
    },
  })
  const r = send.data
  const muted = 'text-xs text-[color:var(--stryzh-color-text-secondary)]'

  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        disabled={send.isPending}
        onClick={() => {
          if (window.confirm(t('leads.detail.notifyTestConfirm'))) send.mutate()
        }}
      >
        {send.isPending ? t('leads.detail.notifyTestSending') : t('leads.detail.notifyTest')}
      </Button>
      <span className={muted}>{t('leads.detail.notifyTestHint')}</span>
      {r ? (
        <span className="text-xs text-[color:var(--stryzh-color-text-primary)]">
          {r.sms === 0
            ? t('leads.detail.notifyTestNoRecipients')
            : t('leads.detail.notifyTestDone', { sms: r.sms, calls: r.calls })}
          {r.sms > 0 && r.calls === 0 ? ` ${t('leads.detail.notifyTestNoCalls')}` : ''}
          {r.waits
            ? ` ${t('leads.detail.notifyTestWaits', {
                at: format(new Date(r.not_before), 'd MMM HH:mm', { locale: dateFnsLocale() }),
              })}`
            : ''}{' '}
          <Link
            to={r.sms === 0 ? '/sber-sms-settings' : '/sber-notify-log'}
            className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
          >
            {r.sms === 0 ? t('nav.sberSmsSettings') : t('nav.sberNotifyLog')}
          </Link>
        </span>
      ) : null}
      {send.isError ? <span className="text-xs text-red-300">{(send.error as Error).message}</span> : null}
    </div>
  )
}
